/**
 * Team Invites Module
 * Handles all workspace invite operations using the new RPCs and tables
 */

import { supabase } from '@/integrations/supabase/client';
import { getEnv } from '@/config/env';

export type InviteRole = 'admin' | 'member' | 'viewer';

export interface WorkspaceInvitation {
  id: string;
  workspace_id: string;
  email: string;
  role: InviteRole;
  invited_by: string;
  token: string;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
}

export interface TeamMember {
  id: string;
  user_id: string;
  workspace_id: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  name: string | null;
  email: string | null;
  full_name?: string | null;
  joined_at?: string | null;
}

export interface CreateInviteResult {
  invitation_id: string;
  token: string;
  expires_at: string;
}

export interface SendInviteEmailPayload {
  workspaceId: string;
  workspaceName: string;
  inviterName: string;
  email: string;
  token: string;
  role?: InviteRole;
}

/**
 * Get workspace invite code (workspaces.invite_code) or legacy join_code
 */
export async function getWorkspaceJoinCode(workspaceId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('workspaces')
    .select('invite_code, join_code')
    .eq('id', workspaceId)
    .single();

  if (error) {
    throw new Error(`Failed to get invite code: ${error.message}`);
  }

  const row = data as { invite_code?: string | null; join_code?: string | null } | null;
  if (!row) return null;
  return row.invite_code ?? row.join_code ?? null;
}

/**
 * Rotate workspace invite code (RPC: rotate_invite_code) or legacy rotate_workspace_join_code
 */
export async function rotateJoinCode(workspaceId: string): Promise<string> {
  const { data, error } = await supabase.rpc('rotate_invite_code', {
    p_workspace_id: workspaceId,
  });

  if (!error && data != null) return (data as string) ?? '';

  const legacy = await supabase.rpc('rotate_workspace_join_code', { p_workspace_id: workspaceId });
  if (!legacy.error && legacy.data != null) return legacy.data as string;

  throw new Error(`Failed to rotate invite code: ${(error || legacy.error)?.message ?? 'Unknown error'}`);
}

/**
 * List team members with profile data (email, name) via get_workspace_members RPC or workspace_team_v.
 * Fallback: show "—" when name/email missing.
 */
export async function listTeamMembers(workspaceId: string): Promise<TeamMember[]> {
  const { data, error } = await supabase.rpc('get_workspace_members', {
    p_workspace_id: workspaceId,
  });

  if (!error && data && Array.isArray(data)) {
    const rows = data as Array<{
      id: string;
      user_id: string;
      workspace_id?: string;
      role: string;
      name: string | null;
      email: string | null;
      full_name?: string | null;
      created_at?: string;
      joined_at?: string;
    }>;
    return rows.map((r) => ({
      id: r.id ?? r.user_id,
      user_id: r.user_id,
      workspace_id: workspaceId,
      role: r.role as TeamMember['role'],
      name: r.name ?? r.full_name ?? null,
      email: r.email ?? null,
      full_name: r.full_name ?? r.name ?? null,
      joined_at: r.joined_at ?? r.created_at ?? null,
    }));
  }

  const fallback = await supabase
    .from('workspace_team_v')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('joined_at', { ascending: false });

  if (fallback.error) {
    throw new Error(`Failed to list team members: ${(error || fallback.error).message}`);
  }

  return (fallback.data || []) as TeamMember[];
}

/**
 * List pending invitations
 */
export async function listPendingInvites(workspaceId: string): Promise<WorkspaceInvitation[]> {
  const { data, error } = await supabase
    .from('workspace_invitations')
    .select('*')
    .eq('workspace_id', workspaceId)
    .is('accepted_at', null)
    .is('revoked_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to list pending invites: ${error.message}`);
  }

  return (data || []) as WorkspaceInvitation[];
}

/** RPC name for create invite (verify matches DB function). */
const CREATE_WORKSPACE_INVITATION_RPC = 'create_workspace_invitation';

/**
 * Create workspace invitation
 */
export async function createInvite(
  workspaceId: string,
  email: string,
  role: InviteRole
): Promise<CreateInviteResult> {
  // Debug: confirm which project and RPC are used (VITE_SUPABASE_URL → projectRef)
  try {
    const url = getEnv().supabaseUrl ?? '';
    const projectRef = url ? new URL(url).hostname?.split('.')[0] ?? 'unknown' : 'unknown';
    if (import.meta.env.DEV) {
      console.log('[createInvite] RPC:', CREATE_WORKSPACE_INVITATION_RPC, '| projectRef:', projectRef, '| url:', url);
    }
  } catch {
    /* ignore */
  }

  const { data, error } = await supabase.rpc(CREATE_WORKSPACE_INVITATION_RPC, {
    p_workspace_id: workspaceId,
    p_email: email.toLowerCase().trim(),
    p_role: role,
  });

  if (error) {
    // Parse common errors
    if (error.message?.includes('seat limit') || error.message?.includes('Seat limit')) {
      throw new Error('SEAT_LIMIT_REACHED');
    }
    if (error.code === '23505') {
      throw new Error('EMAIL_ALREADY_INVITED');
    }
    throw new Error(`Failed to create invite: ${error.message}`);
  }

  // RPC RETURNS TABLE → PostgREST returns array [ { invitation_id, token, expires_at } ]
  const row = Array.isArray(data) ? data[0] : (data as CreateInviteResult | null);
  if (!row?.token) {
    if (import.meta.env.DEV) {
      console.warn('[createInvite] RPC data:', data);
    }
    throw new Error('RPC create_workspace_invitation did not return token');
  }
  return row as CreateInviteResult;
}

/**
 * Revoke workspace invitation
 */
export async function revokeInvite(invitationId: string): Promise<void> {
  const { error } = await supabase.rpc('revoke_workspace_invitation', {
    p_invitation_id: invitationId,
  });

  if (error) {
    throw new Error(`Failed to revoke invite: ${error.message}`);
  }
}

/**
 * Build the accept-invite URL for the client (for copy link / share).
 * Uses VITE_SITE_URL or window.location.origin.
 */
export function getInviteAcceptLink(token: string): string {
  let base = '';
  try {
    base = getEnv().siteUrl?.trim() ?? '';
  } catch {
    // env may throw if not validated
  }
  if (!base && typeof window !== 'undefined') {
    base = window.location.origin;
  }
  base = base.replace(/\/$/, '');
  return `${base}/accept-invite?token=${encodeURIComponent(token)}`;
}

/**
 * Send invite email via Edge Function.
 * Throws on auth/network/Resend errors so caller can show "Invite created, email failed" + Copy link.
 */
export async function sendInviteEmail(payload: SendInviteEmailPayload): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Not authenticated');
  }

  const body = {
    email: payload.email,
    token: payload.token,
    workspaceName: payload.workspaceName ?? 'Decision Sales OS',
    role: payload.role ?? undefined,
  };

  if (!payload.token) {
    throw new Error('Missing token: use invite.token from createInvite result, not invitation_id');
  }

  if (import.meta.env.DEV) {
    console.log('[sendInviteEmail] body:', body);
  }

  const { data, error } = await supabase.functions.invoke('send-workspace-invite', {
    body: {
      ...body,
      role: payload.role ?? 'member', // default safe
    },
  });

  if (error) {
    throw new Error(`Failed to send invite email: ${error.message}`);
  }

  if (!data?.ok) {
    const payload = data as {
      error?: string;
      details?: { message?: string; name?: string };
    };
    const providerMessage = payload?.details?.message || payload?.details?.name;
    const msg = providerMessage || payload?.error || 'Failed to send invite email';
    throw new Error(msg);
  }
}

/**
 * Accept workspace invitation
 */
export async function acceptInvite(token: string): Promise<string> {
  const { data, error } = await supabase.rpc('accept_workspace_invitation', {
    p_token: token,
  });

  if (error) {
    const msg = (error.message ?? '').trim();
    const code = (error as { code?: string }).code;
    if (msg.includes('expired') || msg.includes('Expired')) throw new Error('INVITATION_EXPIRED');
    if (msg.includes('invalid') || msg.includes('Invalid')) throw new Error('INVALID_TOKEN');
    if (msg.includes('email') || msg.includes('Email')) throw new Error('EMAIL_MISMATCH');
    if (code === '409' || code === 'ALREADY_MEMBER') throw new Error('ALREADY_MEMBER');
    if (/already\s+(a\s+)?member|invite\s+already\s+(used|accepted)/i.test(msg)) throw new Error('ALREADY_MEMBER');
    throw new Error(`Failed to accept invite: ${msg}`);
  }

  return data as string; // Returns workspace_id
}

/**
 * Parse Supabase errors into friendly messages
 */
export function parseInviteError(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message;
    
    if (message === 'SEAT_LIMIT_REACHED') {
      return 'Seat limit reached. Please upgrade your plan to invite more members.';
    }
    if (message === 'EMAIL_ALREADY_INVITED') {
      return 'This email has already been invited to this workspace.';
    }
    if (message === 'INVITATION_EXPIRED') {
      return 'This invitation has expired. Please request a new invitation.';
    }
    if (message === 'INVALID_TOKEN') {
      return 'Invalid invitation token. Please check the link and try again.';
    }
    if (message === 'EMAIL_MISMATCH') {
      return 'This invitation was sent to a different email address. Please use the email address that received the invitation.';
    }
    if (message === 'ALREADY_MEMBER') {
      return 'Invite already used. You are in the workspace.';
    }
    
    return message;
  }
  
  return 'An unexpected error occurred. Please try again.';
}

